import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  StepperDataPayload,
  StepperDataResponse,
  StepperDataService,
} from './stepper-data.service';

interface StepperDataState {
  draftExists: boolean;
  draft: StepperDataResponse | null;
}

const initialState: StepperDataState = {
  draftExists: false,
  draft: null,
};

export const StepperDataStore = signalStore(
  withState(initialState),
  withMethods((store, service = inject(StepperDataService)) => ({
    loadDraft: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { draftExists: false, draft: null })),
        switchMap(stepperName =>
          service.getStepperData(stepperName).pipe(
            tap(({ res }) =>
              patchState(store, { draftExists: true, draft: res })
            ),
            catchError(() => {
              patchState(store, { draftExists: false, draft: null });
              return of(null);
            })
          )
        )
      )
    ),

    saveDraft: rxMethod<StepperDataPayload>(
      pipe(
        switchMap(payload => {
          const wasExisting = store.draftExists();
          return (
            wasExisting
              ? service.updateStepperData(payload)
              : service.createStepperData(payload)
          ).pipe(
            tap(({ res }) =>
              patchState(store, { draftExists: true, draft: res })
            ),
            catchError(() => {
              if (wasExisting) {
                patchState(store, { draftExists: false, draft: null });
                return service.createStepperData(payload).pipe(
                  tap(({ res }) =>
                    patchState(store, { draftExists: true, draft: res })
                  ),
                  catchError(() => of(null))
                );
              }
              return of(null);
            })
          );
        })
      )
    ),

    deleteDraft: rxMethod<string>(
      pipe(
        switchMap(stepperName =>
          service.deleteStepperData(stepperName).pipe(
            tap(() => patchState(store, { draftExists: false, draft: null })),
            catchError(() => of(null))
          )
        )
      )
    ),
  }))
);
